import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Cookies from 'js-cookie';
import api from '@services/api';
import type { AuthProviderProps, AuthProviderType } from './types';

const Context = createContext({} as AuthProviderType);
const useAuthContext = (): AuthProviderType => useContext(Context);

const AuthProvider = ({ children }:AuthProviderProps) => {
  const [roles, setRoles] = useState<string[]>([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(() => !!Cookies.get('accessToken'));

  const refreshPermissions = useCallback(async () => {
    if (!Cookies.get('accessToken')) return;
    try {
      const { data } = await api.get('/account/me');
      setRoles(data.permissions ?? []);
    } catch {
      // 401 is handled globally by the api interceptor
    } finally {
      setIsLoadingPermissions(false);
    }
  }, []);

  useEffect(() => {
    refreshPermissions();

    let hiddenAt: number | null = null;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      } else if (document.visibilityState === 'visible' && hiddenAt !== null) {
        if (Date.now() - hiddenAt > 15_000) {
          refreshPermissions();
        }
        hiddenAt = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshPermissions]);

  const checkIfHasRole = useCallback((role:string) => {
    return roles.includes(role);
  }, [roles]);

  const value = useMemo(() => ({
    roles,
    setRoles,
    checkIfHasRole,
    refreshPermissions,
    isLoadingPermissions,
  }), [checkIfHasRole, isLoadingPermissions, refreshPermissions, roles]);

  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  );
};

export { AuthProvider, useAuthContext };
